import { useEffect, useState } from "react"
import classNames from "classnames"
import { v4 as uuidv4 } from "uuid"

import { useLazyGetAlertAndDeviceWidgetQuery, useLazyGetAlertTypesWidgetQuery } from "store/api/alertsApi"
import { ApiTypes } from "types"

import { Spinner } from "components/common/Spinner"
import { Tooltip } from "components/common/Tooltip"
import { dummyAlertsWidgetData } from "pages/contractor/AlertsPage/dummyAlertsData"

import styles from "./index.module.scss"

interface WidgetAlertsProps {
  updateIndex: number
  hasAlertsAccess: boolean
}

const initialWidgetAlertsData: ApiTypes.Model.AlertsWidgetData = {
  airQuality: 0,
  ambientSensorMalfunction: 0,
  fanMalfunction: 0,
  humiditySensorMalfunction: 0,
  offline: 0,
  efficiency_issue: 0,
  temperatureSensorMalfunction: 0,
  enrolledDevicesCount: 0,
  devicesWithActiveAlerts: 0,
  alertsCount: 0,
}

export const WidgetAlerts = ({ updateIndex, hasAlertsAccess }: WidgetAlertsProps) => {
  const [widgetData, setWidgetData] = useState(initialWidgetAlertsData)
  const [triggerGetAlertTypesWidget, { isFetching: isFetching }] = useLazyGetAlertTypesWidgetQuery()
  const [triggerGetAlertsAndDeviceWidget] = useLazyGetAlertAndDeviceWidgetQuery()

  useEffect(() => {
    if (!hasAlertsAccess) {
      setWidgetData(dummyAlertsWidgetData)
      return
    }

    const fetchData = async () => {
      try {
        const [res1, res2] = await Promise.all([triggerGetAlertTypesWidget(), triggerGetAlertsAndDeviceWidget()])

        let updatedData = { ...widgetData }

        if (res1?.data?.data) {
          updatedData = {
            ...updatedData,
            airQuality: res1.data.data?.air_quality,
            ambientSensorMalfunction: res1.data.data?.ambient_sensor_malfunction,
            fanMalfunction: res1.data.data?.fan_malfunction,
            humiditySensorMalfunction: res1.data.data?.humidity_sensor_malfunction,
            offline: res1.data.data?.offline,
            efficiency_issue: res1.data.data?.efficiency_issue,
            temperatureSensorMalfunction: res1.data.data?.temperature_sensor_malfunction,
          }
        }

        if (res2?.data?.data) {
          updatedData = {
            ...updatedData,
            enrolledDevicesCount: res2.data.data?.enrolled_devices_count,
            devicesWithActiveAlerts: res2.data.data?.devices_with_active_alerts,
            alertsCount: res2.data.data?.alerts_count,
          }
        }

        setWidgetData(updatedData)
      } catch (error) {
        console.error(error)
      }
    }

    fetchData()
  }, [updateIndex])

  return (
    <div className={classNames(styles.alertsWidgetWrapper)}>
      <header className={styles.widgetHeader}>
        <h3 className={styles.widgetTitle}>Alerts</h3>
        <div className={styles.summaryIndicatorsWrapper}>
          <span className={styles.summaryIndicators}>
            <i>{widgetData.enrolledDevicesCount}</i> Installed Devices
          </span>
          <span className={styles.summaryIndicators}>
            <i>{widgetData.devicesWithActiveAlerts}</i> Devices With Alerts
          </span>
          <span className={styles.summaryIndicators}>
            <i>{widgetData.alertsCount}</i> Alerts
          </span>
        </div>
      </header>
      <div className={styles.dataWrapper}>
        <div className={styles.indicatorsWrapper}>
          <span className={styles.indicators}>
            <Tooltip id={uuidv4()} text="System Efficiency">
              System failed to reach the set temperature
            </Tooltip>
            <i>{widgetData.efficiency_issue}</i>
          </span>
          <span className={styles.indicators}>
            Air Quality Sensor Malfunction <i>{widgetData.airQuality}</i>
          </span>
          <span className={styles.indicators}>
            Ambient Sensor Malfunction <i>{widgetData.ambientSensorMalfunction}</i>
          </span>
          <span className={styles.indicators}>
            Fan Malfunction <i>{widgetData.fanMalfunction}</i>
          </span>
          <span className={styles.indicators}>
            Humidity Sensor Malfunction <i>{widgetData.humiditySensorMalfunction}</i>
          </span>
          <span className={styles.indicators}>
            Temperature Sensor Malfunction <i>{widgetData.temperatureSensorMalfunction}</i>
          </span>
        </div>
        {isFetching && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </div>
  )
}
